import { forwardRef } from 'react';
import { SvgIcon, SvgIconProps } from '@mui/material';
import {
  ArrowLeft,
  Box,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  Copy,
  Crop,
  Folder,
  Grid2X2,
  Image,
  ImagePlus,
  Info,
  List,
  ListFilter,
  ListPlus,
  LocateFixed,
  LockKeyhole,
  LogOut,
  MapPin,
  MapPinPen,
  Link,
  Menu,
  Minus,
  MoreVertical,
  Palette,
  Pencil,
  Plus,
  Replace,
  RotateCcw,
  RotateCw,
  Search,
  Share2,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Square,
  SquareCheck,
  SquareDashed,
  SwitchCamera,
  SquareMinus,
  SquareMousePointer,
  Trash2,
  TriangleAlert,
  User,
  UserCircle,
  Users,
  Tag,
  UserPlus,
  WalletCards,
  X,
  type LucideIcon,
} from 'lucide-react';

export const CONTROL_ICON_SIZE = 20;
export const NAVIGATION_ICON_SIZE = 24;

/** A single SVG: Lucide geometry with MUI palette, sx, slot classes and refs. */
function appIcon(Icon: LucideIcon, name: string) {
  const Component = forwardRef<SVGSVGElement, SvgIconProps>(function AppIcon(
    { sx, ...props },
    ref,
  ) {
    return (
      <SvgIcon
        component={Icon}
        inheritViewBox
        ref={ref}
        strokeWidth={2.25}
        data-testid={name}
        {...props}
        sx={[
          {
            fill: 'none',
            verticalAlign: 'middle',
            fontSize:
              props.fontSize === 'inherit'
                ? 'inherit'
                : props.fontSize === 'large'
                  ? 28
                  : CONTROL_ICON_SIZE,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      />
    );
  });
  Component.displayName = name;
  return Component;
}

export const AddIcon = appIcon(Plus, 'AddIcon');
export const BackIcon = appIcon(ArrowLeft, 'BackIcon');
export const CameraIcon = appIcon(Camera, 'CameraIcon');
export const CameraSwitchIcon = appIcon(SwitchCamera, 'CameraSwitchIcon');
export const CheckIcon = appIcon(Check, 'CheckIcon');
export const CheckboxCheckedIcon = appIcon(SquareCheck, 'CheckboxCheckedIcon');
export const CheckboxIcon = appIcon(Square, 'CheckboxIcon');
export const CheckboxIndeterminateIcon = appIcon(
  SquareMinus,
  'CheckboxIndeterminateIcon',
);
export const ChevronDownIcon = appIcon(ChevronDown, 'ChevronDownIcon');
export const ChevronRightIcon = appIcon(ChevronRight, 'ChevronRightIcon');
export const ChevronUpIcon = appIcon(ChevronUp, 'ChevronUpIcon');
export const CloseIcon = appIcon(X, 'CloseIcon');
export const CopyIcon = appIcon(Copy, 'CopyIcon');
export const CropIcon = appIcon(Crop, 'CropIcon');
export const DeleteIcon = appIcon(Trash2, 'DeleteIcon');
export const DeselectIcon = appIcon(SquareDashed, 'DeselectIcon');
export const EditIcon = appIcon(Pencil, 'EditIcon');
export const EditLocationIcon = appIcon(MapPinPen, 'EditLocationIcon');
export const ErrorIcon = appIcon(CircleAlert, 'ErrorIcon');
export const FilterIcon = appIcon(SlidersHorizontal, 'FilterIcon');
export const FindReplaceIcon = appIcon(Replace, 'FindReplaceIcon');
export const FolderIcon = appIcon(Folder, 'FolderIcon');
export const GridIcon = appIcon(Grid2X2, 'GridIcon');
export const ImageIcon = appIcon(Image, 'ImageIcon');
export const ImagePlusIcon = appIcon(ImagePlus, 'ImagePlusIcon');
export const InfoIcon = appIcon(Info, 'InfoIcon');
export const InventoryIcon = appIcon(Box, 'InventoryIcon');
export const ListIcon = appIcon(List, 'ListIcon');
export const LinkIcon = appIcon(Link, 'LinkIcon');
export const ListPlusIcon = appIcon(ListPlus, 'ListPlusIcon');
export const LocateIcon = appIcon(LocateFixed, 'LocateIcon');
export const LocationIcon = appIcon(MapPin, 'LocationIcon');
export const LockIcon = appIcon(LockKeyhole, 'LockIcon');
export const LogoutIcon = appIcon(LogOut, 'LogoutIcon');
export const MenuIcon = appIcon(Menu, 'MenuIcon');
export const MoreIcon = appIcon(MoreVertical, 'MoreIcon');
export const PaletteIcon = appIcon(Palette, 'PaletteIcon');
export const RemoveIcon = appIcon(Minus, 'RemoveIcon');
export const RestoreIcon = appIcon(RotateCcw, 'RestoreIcon');
export const SearchIcon = appIcon(Search, 'SearchIcon');
export const ShareIcon = appIcon(Share2, 'ShareIcon');
export const SelectAllIcon = appIcon(SquareMousePointer, 'SelectAllIcon');
export const SettingsIcon = appIcon(Settings, 'SettingsIcon');
export const SortIcon = appIcon(ListFilter, 'SortIcon');
export const SparklesIcon = appIcon(Sparkles, 'SparklesIcon');
export const SuccessIcon = appIcon(CircleCheck, 'SuccessIcon');
export const SyncIcon = appIcon(RotateCw, 'SyncIcon');
export const TagIcon = appIcon(Tag, 'TagIcon');
export const UserPlusIcon = appIcon(UserPlus, 'UserPlusIcon');
export const UserIcon = appIcon(User, 'UserIcon');
export const UserCircleIcon = appIcon(UserCircle, 'UserCircleIcon');
export const UsersIcon = appIcon(Users, 'UsersIcon');
export const WalletIcon = appIcon(WalletCards, 'WalletIcon');
export const WarningIcon = appIcon(TriangleAlert, 'WarningIcon');
